// PortfolioDetailsPage.js
import React, { useEffect, useState } from "react";
import { TrendingUp, AlertTriangle } from "lucide-react";
import PortfoliosService from "../../../services/api/admin.userportfolios.service";

// Modified to accept portfolioId and onBackClick as props and handle both product types
const PortfolioDetailsPage = ({ portfolioId, onBackClick }) => {
  const [portfolio, setPortfolio] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [investmentHistory, setInvestmentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPortfolioDetails = async () => {
      setLoading(true);
      try {
        const data = await PortfoliosService.fetchPortfolioDetails(portfolioId);
        setPortfolio(data.portfolio);
        setHoldings(data.holdings);
        setInvestmentHistory(data.investmentHistory);
        setError(null);
      } catch (error) {
        setError(error.message || "Failed to load portfolio details");
      } finally {
        setLoading(false);
      }
    };

    if (portfolioId) {
      loadPortfolioDetails();
    }
  }, [portfolioId]);

  // Function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get profit status color class
  const getProfitColorClass = (profit) => {
    if (profit > 0) return "text-green-600";
    if (profit < 0) return "text-red-600";
    return "text-gray-600";
  };

  // Get profit icon
  const getProfitIcon = (profit) => {
    if (profit > 0)
      return <TrendingUp className="ml-1 w-4 h-4 text-green-600" />;
    if (profit < 0)
      return (
        <TrendingUp className="ml-1 w-4 h-4 text-red-600 transform rotate-180" />
      );
    return null;
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    if (status === "ACTIVE") return "bg-green-100 text-green-800";
    return "bg-red-100 text-red-800";
  };

  // Render holdings based on portfolio type
  const renderHoldings = () => {
    if (!holdings || holdings.length === 0) {
      return (
        <p className="text-gray-500">
          No holdings available for this portfolio
        </p>
      );
    }

    const isMutualFund = portfolio?.portfolioType === "MUTUAL FUND";

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {isMutualFund ? (
                <>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scheme Name
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fund House
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Units
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current NAV
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Value
                  </th>
                </>
              ) : (
                <>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Industry
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sector
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price Per Share
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Value
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {holdings.map((holding) => (
              <tr key={holding._id} className="hover:bg-gray-50">
                {isMutualFund ? (
                  <>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {holding.productDetails?.name || "N/A"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {holding.productDetails?.schemeCode
                          ? `#${holding.productDetails.schemeCode}`
                          : ""}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {holding.productDetails?.fundHouse || "N/A"}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {holding.productDetails?.category || "N/A"}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {holding.productDetails?.type || "N/A"}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {holding.quantity?.toLocaleString() || "0"}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        ₹
                        {holding.navDetails?.currentNav?.toLocaleString() ||
                          "0"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {holding.navDetails?.navDate
                          ? formatDate(holding.navDetails.navDate)
                          : ""}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        ₹
                        {(
                          holding.quantity * holding.navDetails?.currentNav
                        )?.toLocaleString() || "0"}
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {holding.productId?.basicData?.legalName || "N/A"}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {holding.productId?.basicData?.industry || "N/A"}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {holding.productId?.basicData?.sector || "N/A"}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {holding.quantity?.toLocaleString() || "0"}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        ₹
                        {holding.currentValuation?.pricePerShare?.toLocaleString() ||
                          "0"}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        ₹
                        {(
                          holding.quantity *
                          holding.currentValuation?.pricePerShare
                        )?.toLocaleString() || "0"}
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render fund performance section for mutual funds
  const renderFundPerformance = () => {
    if (
      portfolio?.portfolioType !== "MUTUAL FUND" ||
      !holdings ||
      holdings.length === 0
    ) {
      return null;
    }

    return (
      <div className="p-6 border-t">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Fund Performance
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {holdings.map((holding) => (
            <div
              key={`performance-${holding._id}`}
              className="bg-gray-50 p-4 rounded-lg"
            >
              <h4 className="font-medium text-gray-800 mb-3">
                {holding.productDetails?.name || "Fund"}
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">1 Month Return</p>
                  <p
                    className={`font-medium ${getProfitColorClass(
                      holding.performance?.["1m"]?.absoluteReturn || 0
                    )}`}
                  >
                    {holding.performance?.["1m"]?.absoluteReturn?.toFixed(2) ||
                      "0.00"}
                    %
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">3 Month Return</p>
                  <p
                    className={`font-medium ${getProfitColorClass(
                      holding.performance?.["3m"]?.absoluteReturn || 0
                    )}`}
                  >
                    {holding.performance?.["3m"]?.absoluteReturn?.toFixed(2) ||
                      "0.00"}
                    %
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">1 Year Return</p>
                  <p
                    className={`font-medium ${getProfitColorClass(
                      holding.performance?.["1y"]?.annualizedReturn || 0
                    )}`}
                  >
                    {holding.performance?.["1y"]?.annualizedReturn?.toFixed(
                      2
                    ) || "0.00"}
                    %
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">3 Year Return</p>
                  <p
                    className={`font-medium ${getProfitColorClass(
                      holding.performance?.["3y"]?.annualizedReturn || 0
                    )}`}
                  >
                    {holding.performance?.["3y"]?.annualizedReturn?.toFixed(
                      2
                    ) || "0.00"}
                    %
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">5 Year Return</p>
                  <p
                    className={`font-medium ${getProfitColorClass(
                      holding.performance?.["5y"]?.annualizedReturn || 0
                    )}`}
                  >
                    {holding.performance?.["5y"]?.annualizedReturn?.toFixed(
                      2
                    ) || "0.00"}
                    %
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading portfolio details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-red-600 text-center">
        <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
        <p>{error}</p>
        <button
          onClick={onBackClick}
          className="mt-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors"
        >
          Back to List
        </button>
      </div>
    );
  }

  if (!portfolio) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600">Portfolio not found</p>
        <button
          onClick={onBackClick}
          className="mt-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors"
        >
          Back to List
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 ">

         <button
              onClick={onBackClick}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-1 px-3 rounded-lg transition-colors"
            >
              Back to List
            </button>
      <div className="overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                {portfolio.portfolioName}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Created {formatDate(portfolio.createdAt)} | Last updated{" "}
                {formatDate(portfolio.updatedAt)}
              </p>
            </div>
         
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Portfolio Information
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-500">Type</p>
                  <p className="font-medium">{portfolio.portfolioType}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Risk Profile</p>
                  <p className="font-medium">{portfolio.riskProfile}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Strategy</p>
                  <p className="font-medium">
                    {portfolio.investmentStrategy?.replace("_", " ") || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(
                      portfolio.status
                    )}`}
                  >
                    {portfolio.status}
                  </span>
                </div>
              </div>

              <h4 className="text-sm font-semibold text-gray-800 mb-2">
                Financial Summary
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Total Investment</p>
                  <p className="font-medium">
                    ₹
                    {portfolio.summary?.totalInvestment?.toLocaleString() ||
                      "0"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Current Value</p>
                  <p className="font-medium">
                    ₹{portfolio.summary?.currentValue?.toLocaleString() || "0"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Profit/Loss</p>
                  <p
                    className={`font-medium flex items-center ${getProfitColorClass(
                      portfolio.summary?.totalProfit || 0
                    )}`}
                  >
                    ₹{portfolio.summary?.totalProfit?.toLocaleString() || "0"}
                    {getProfitIcon(portfolio.summary?.totalProfit || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Profit Percentage</p>
                  <p
                    className={`font-medium ${getProfitColorClass(
                      portfolio.summary?.profitPercentage || 0
                    )}`}
                  >
                    {portfolio.summary?.profitPercentage?.toFixed(2) || "0.00"}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              User Information
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              {portfolio.userData ? (
                <>
                  <div className="mb-4">
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium">
                      {portfolio?.userData?.fullName}{" "}
                    </p>
                  </div>
                  <div className="mb-4">
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{portfolio.userData.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium">
                      {portfolio?.userData?.mobileNo || "N/A"}
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-gray-500">No user data available</p>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 border-t">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Holdings</h3>
          {renderHoldings()}
        </div>

        {renderFundPerformance()}

        <div className="p-6 border-t">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Recent Investment History
          </h3>
          {investmentHistory && investmentHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Value
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Change
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Change %
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {investmentHistory.map((history) => (
                    <tr key={history._id} className="hover:bg-gray-50">
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(history.date)}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          ₹
                          {history.valuation?.totalValue?.toLocaleString() ||
                            "0"}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div
                          className={`text-sm font-medium ${getProfitColorClass(
                            history.valuation?.changeFromPrevious || 0
                          )}`}
                        >
                          ₹
                          {history.valuation?.changeFromPrevious?.toLocaleString() ||
                            "0"}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div
                          className={`text-sm font-medium ${getProfitColorClass(
                            history.valuation?.changePercentage || 0
                          )}`}
                        >
                          {history.valuation?.changePercentage?.toFixed(2) ||
                            "0.00"}
                          %
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500">No investment history available</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortfolioDetailsPage;
